export type AgentHost = 'codex' | 'claude' | 'claude-code' | 'claude-desktop'

export interface AgentConfigOptions {
  apiKey?: string
  packageSpec?: string
  browserProfileName?: string
  browserProfileSaveChanges?: boolean
}

function apiKeyValue(options: AgentConfigOptions): string {
  return options.apiKey?.trim() || 'sk_live_your_key'
}

function packageSpec(options: AgentConfigOptions): string {
  return options.packageSpec?.trim() || 'mcp-scraper@latest'
}

export function combinedNpxArgs(options: AgentConfigOptions = {}): string[] {
  return ['-y', '-p', packageSpec(options), 'mcp-scraper']
}

function envConfig(options: AgentConfigOptions): Record<string, string> {
  const env: Record<string, string> = { MCP_SCRAPER_API_KEY: apiKeyValue(options) }
  const profileName = options.browserProfileName?.trim()
  if (profileName) env.BROWSER_AGENT_PROFILE_NAME = profileName
  if (options.browserProfileSaveChanges === true) env.BROWSER_AGENT_PROFILE_SAVE_CHANGES = 'true'
  return env
}

function tomlInlineTable(value: Record<string, string>): string {
  const entries = Object.entries(value).map(([key, item]) => `${key} = ${JSON.stringify(item)}`)
  return `{ ${entries.join(', ')} }`
}

export function renderCodexConfig(options: AgentConfigOptions = {}): string {
  return [
    '[mcp_servers.mcp-scraper]',
    'command = "npx"',
    `args = ${JSON.stringify(combinedNpxArgs(options))}`,
    `env = ${tomlInlineTable(envConfig(options))}`,
  ].join('\n')
}

export function renderClaudeCommand(options: AgentConfigOptions = {}): string {
  const env = Object.entries(envConfig(options)).map(([key, value]) => `  --env ${key}=${value}`)
  return [
    'claude mcp add mcp-scraper --scope user',
    ...env,
    `  -- npx ${combinedNpxArgs(options).join(' ')}`,
  ].join(' \\\n')
}

export function claudeMcpRemoveArgs(): string[] {
  return ['mcp', 'remove', 'mcp-scraper', '-s', 'user']
}

export function claudeMcpAddArgs(options: AgentConfigOptions = {}): string[] {
  const args = ['mcp', 'add', 'mcp-scraper', '--scope', 'user']
  for (const [key, value] of Object.entries(envConfig(options))) {
    args.push('--env', `${key}=${value}`)
  }
  args.push('--', 'npx', ...combinedNpxArgs(options))
  return args
}

export function normalizeAgentHost(host: string): AgentHost {
  if (host === 'claude-code') return 'claude'
  if (host === 'claude' || host === 'codex' || host === 'claude-desktop') return host
  throw new Error('Unknown host "' + host + '". Use: codex, claude, claude-code, or claude-desktop')
}

export function renderClaudeDesktopConfig(options: AgentConfigOptions = {}): string {
  return JSON.stringify({
    mcpServers: {
      'mcp-scraper': {
        command: 'npx',
        args: combinedNpxArgs(options),
        env: envConfig(options),
      },
    },
  }, null, 2)
}

export function renderAgentInstall(host: AgentHost, options: AgentConfigOptions = {}): string {
  const normalizedHost = normalizeAgentHost(host)
  const restart = 'Restart the MCP client so it starts a fresh npx process.'
  const applyCommand = `MCP_SCRAPER_API_KEY=${apiKeyValue(options)} npx -y -p mcp-scraper@latest mcp-scraper-cli agent install claude --apply`
  if (normalizedHost === 'codex') {
    return [
      '# Codex MCP config',
      renderCodexConfig(options),
      '',
      restart,
    ].join('\n')
  }
  if (normalizedHost === 'claude') {
    return [
      '# Claude Code command',
      renderClaudeCommand(options),
      '',
      '# One-command Claude Code setup',
      applyCommand,
      '',
      restart,
    ].join('\n')
  }
  return [
    '# Claude Desktop config',
    renderClaudeDesktopConfig(options),
    '',
    'Desktop Extension: https://mcpscraper.dev/downloads/mcp-scraper.mcpb',
    restart,
  ].join('\n')
}
